import type { Shot } from "../types/shot.types";

/**
 * Inserts a shot, or reconciles it with the one already held under the same id.
 *
 * Launch and final measurements can be applied in either order, so the merge is
 * driven by status rather than arrival: a landed shot is authoritative for
 * measurements, while the launch record is authoritative for consent and timing
 * (consent is captured per player at launch, and the launch timestamp anchors
 * the snapshot lookup).
 */
export function upsertShot(
  shots: Shot[],
  incoming: Shot,
  maxShots = Number.POSITIVE_INFINITY
): Shot[] {
  const index = shots.findIndex((shot) => shot.id === incoming.id);
  if (index === -1) {
    const next = [...shots, incoming];
    return next.length > maxShots ? next.slice(next.length - maxShots) : next;
  }

  const existing = shots[index];
  if (existing.status === "landed" && incoming.status === "flying") {
    return shots;
  }

  const merged: Shot = {
    ...existing,
    ...incoming,
    hasConsent: existing.hasConsent ?? incoming.hasConsent,
    timestamp: existing.timestamp,
    localCreatedAt: existing.localCreatedAt,
    claimed: existing.claimed ?? incoming.claimed,
  };

  return [...shots.slice(0, index), merged, ...shots.slice(index + 1)];
}
